"use client";

import { c as _c } from "react/compiler-runtime";
import { keepPreviousData, useQuery } from "@tanstack/react-query";
import { CommunityMemberOverview } from "@/entities/IProfile";
import { Page } from "@/helpers/Types";
import { CommunityMembersQuery } from "@/app/network/page";
import { useCallback, useEffect, useMemo, useState } from "react";
import { commonApiFetch } from "@/services/api/common-api";
import { SortDirection } from "@/entities/ISort";
import CommunityMembersTable from "./members-table/CommunityMembersTable";
import { usePathname, useSearchParams, useRouter } from "next/navigation";
import { useDebounce } from "react-use";
import CommonCardSkeleton from "../utils/animation/CommonCardSkeleton";
import { useSelector } from "react-redux";
import { selectActiveGroupId } from "@/store/groupSlice";
import CommonTablePagination from "@/components/utils/table/paginator/CommonTablePagination";
import { CommunityMembersSortOption } from "@/enums";
import { QueryKey } from "@/components/react-query-wrapper/ReactQueryWrapper";
import { useSetTitle } from "@/contexts/TitleContext";
interface QueryUpdateInput {
  name: keyof typeof SEARCH_PARAMS_FIELDS;
  value: string | null;
}
const SEARCH_PARAMS_FIELDS = {
  page: "page",
  sortBy: "sort-by",
  sortDirection: "sort-direction",
  group: "group"
} as const;
export default function CommunityMembers() {
  const $ = _c(86);
  useSetTitle("Network");
  const defaultSortBy = CommunityMembersSortOption.LEVEL;
  const defaultSortDirection = SortDirection.DESC;
  const defaultPageSize = 50;
  const defaultPage = 1;
  const router = useRouter();
  const pathname = usePathname();
  const searchParams = useSearchParams();
  const activeGroupId = useSelector(selectActiveGroupId);
  let t0;
  if ($[0] === Symbol.for("react.memo_cache_sentinel")) {
    t0 = sort => {
      if (!sort) {
        return defaultSortBy;
      }
      if (Object.values(CommunityMembersSortOption).includes(sort.toLowerCase() as any)) {
        return sort.toLowerCase() as CommunityMembersSortOption;
      }
      return defaultSortBy;
    };
    $[0] = t0;
  } else {
    t0 = $[0];
  }
  const convertSortBy = t0;
  let t1;
  if ($[1] === Symbol.for("react.memo_cache_sentinel")) {
    t1 = sortDirection => {
      if (!sortDirection) {
        return defaultSortDirection;
      }
      if (Object.values(SortDirection).includes(sortDirection.toUpperCase() as any)) {
        return sortDirection.toUpperCase() as SortDirection;
      }
      return defaultSortDirection;
    };
    $[1] = t1;
  } else {
    t1 = $[1];
  }
  const convertSortDirection = t1;
  let t2;
  if ($[2] !== searchParams) {
    t2 = searchParams?.get(SEARCH_PARAMS_FIELDS.page) || "";
    $[2] = searchParams;
    $[3] = t2;
  } else {
    t2 = $[3];
  }
  const page = parseInt(t2);
  let group;
  let t3;
  let t4;
  let t5;
  let t6;
  if ($[4] !== page || $[5] !== searchParams) {
    const sortBy = searchParams?.get(SEARCH_PARAMS_FIELDS.sortBy);
    const sortDirection_0 = searchParams?.get(SEARCH_PARAMS_FIELDS.sortDirection);
    let t7;
    if ($[11] !== searchParams) {
      t7 = searchParams?.get(SEARCH_PARAMS_FIELDS.group);
      $[11] = searchParams;
      $[12] = t7;
    } else {
      t7 = $[12];
    }
    group = t7;
    t3 = page || 1;
    t4 = 50;
    t5 = sortBy ? convertSortBy(sortBy) : defaultSortBy;
    t6 = sortDirection_0 ? convertSortDirection(sortDirection_0) : defaultSortDirection;
    $[4] = page;
    $[5] = searchParams;
    $[6] = group;
    $[7] = t3;
    $[8] = t4;
    $[9] = t5;
    $[10] = t6;
  } else {
    group = $[6];
    t3 = $[7];
    t4 = $[8];
    t5 = $[9];
    t6 = $[10];
  }
  let query;
  if ($[13] !== group || $[14] !== t3 || $[15] !== t4 || $[16] !== t5 || $[17] !== t6) {
    query = {
      page: t3,
      page_size: t4,
      sort: t5,
      sort_direction: t6
    };
    if (group) {
      query.group_id = group;
    }
    $[13] = group;
    $[14] = t3;
    $[15] = t4;
    $[16] = t5;
    $[17] = t6;
    $[18] = query;
  } else {
    query = $[18];
  }
  const params = query;
  let t7;
  if ($[19] !== searchParams) {
    t7 = (updateItems, t8) => {
      const lowerCase = t8 === undefined ? true : t8;
      const searchParamsStr = new URLSearchParams(searchParams?.toString());
      for (const {
        name,
        value
      } of updateItems) {
        const key = SEARCH_PARAMS_FIELDS[name];
        if (!value) {
          searchParamsStr.delete(key);
        } else {
          searchParamsStr.set(key, lowerCase ? value.toLowerCase() : value);
        }
      }
      return searchParamsStr.toString();
    };
    $[19] = searchParams;
    $[20] = t7;
  } else {
    t7 = $[20];
  }
  const createQueryString = t7;
  let t8;
  if ($[21] === Symbol.for("react.memo_cache_sentinel")) {
    t8 = t9 => {
      const {
        newSortBy,
        currentSortBy,
        currentSortDirection
      } = t9;
      if (newSortBy === currentSortBy) {
        if (currentSortDirection === SortDirection.ASC) {
          return SortDirection.DESC;
        }
        return SortDirection.ASC;
      }
      return defaultSortDirection;
    };
    $[21] = t8;
  } else {
    t8 = $[21];
  }
  const calculateSortDirection = t8;
  let t9;
  if ($[22] !== params) {
    t9 = () => params;
    $[22] = params;
    $[23] = t9;
  } else {
    t9 = $[23];
  }
  const [debouncedParams, setDebouncedParams] = useState(t9);
  let t10;
  if ($[24] !== params) {
    t10 = () => setDebouncedParams(params);
    $[24] = params;
    $[25] = t10;
  } else {
    t10 = $[25];
  }
  let t11;
  if ($[26] !== params) {
    t11 = [params];
    $[26] = params;
    $[27] = t11;
  } else {
    t11 = $[27];
  }
  useDebounce(t10, 200, t11);
  let t12;
  if ($[28] !== debouncedParams.group_id || $[29] !== debouncedParams.page || $[30] !== debouncedParams.page_size || $[31] !== debouncedParams.sort || $[32] !== debouncedParams.sort_direction) {
    t12 = [QueryKey.COMMUNITY_MEMBERS_TOP, {
      page: debouncedParams.page,
      pageSize: debouncedParams.page_size,
      sort: debouncedParams.sort,
      sortDirection: debouncedParams.sort_direction,
      groupId: debouncedParams.group_id
    }];
    $[28] = debouncedParams.group_id;
    $[29] = debouncedParams.page;
    $[30] = debouncedParams.page_size;
    $[31] = debouncedParams.sort;
    $[32] = debouncedParams.sort_direction;
    $[33] = t12;
  } else {
    t12 = $[33];
  }
  let t13;
  if ($[34] !== debouncedParams) {
    t13 = async () => await commonApiFetch({
      endpoint: "community-members/top",
      params: debouncedParams
    });
    $[34] = debouncedParams;
    $[35] = t13;
  } else {
    t13 = $[35];
  }
  let t14;
  if ($[36] !== t12 || $[37] !== t13) {
    t14 = {
      queryKey: t12,
      queryFn: t13,
      placeholderData: keepPreviousData
    };
    $[36] = t12;
    $[37] = t13;
    $[38] = t14;
  } else {
    t14 = $[38];
  }
  const {
    isLoading,
    isFetching,
    data: members
  } = useQuery(t14);
  let t15;
  if ($[39] !== createQueryString || $[40] !== pathname || $[41] !== router) {
    t15 = (updateItems_0, t16) => {
      const lowerCase_0 = t16 === undefined ? true : t16;
      const queryString = createQueryString(updateItems_0, lowerCase_0);
      const path = queryString ? `${pathname}?${queryString}` : pathname;
      if (path) {
        router.replace(path);
      }
    };
    $[39] = createQueryString;
    $[40] = pathname;
    $[41] = router;
    $[42] = t15;
  } else {
    t15 = $[42];
  }
  const updateFields = t15;
  let t16;
  if ($[43] !== debouncedParams.sort || $[44] !== debouncedParams.sort_direction || $[45] !== updateFields) {
    t16 = async sortBy_0 => {
      const items = [{
        name: "sortBy",
        value: sortBy_0
      }, {
        name: "sortDirection",
        value: calculateSortDirection({
          newSortBy: sortBy_0,
          currentSortBy: debouncedParams.sort,
          currentSortDirection: debouncedParams.sort_direction
        })
      }, {
        name: "page",
        value: "1"
      }];
      await updateFields(items);
    };
    $[43] = debouncedParams.sort;
    $[44] = debouncedParams.sort_direction;
    $[45] = updateFields;
    $[46] = t16;
  } else {
    t16 = $[46];
  }
  const setSortBy = t16;
  let t17;
  let t18;
  if ($[47] !== activeGroupId || $[48] !== params.group_id || $[49] !== updateFields) {
    t17 = () => {
      const urlGroup = params.group_id ?? null;
      const stateGroup = activeGroupId ?? null;
      if (urlGroup === stateGroup) {
        return;
      }
      const items_0 = [{
        name: "group",
        value: stateGroup
      }, {
        name: "page",
        value: "1"
      }];
      updateFields(items_0, false);
    };
    t18 = [activeGroupId, params.group_id, updateFields];
    $[47] = activeGroupId;
    $[48] = params.group_id;
    $[49] = updateFields;
    $[50] = t17;
    $[51] = t18;
  } else {
    t17 = $[50];
    t18 = $[51];
  }
  useEffect(t17, t18);
  let t19;
  if ($[52] !== updateFields) {
    t19 = async page_0 => {
      const items_1 = [{
        name: "page",
        value: page_0.toString()
      }];
      await updateFields(items_1);
    };
    $[52] = updateFields;
    $[53] = t19;
  } else {
    t19 = $[53];
  }
  const setPage = t19;
  const [totalPages, setTotalPages] = useState(1);
  let t20;
  if ($[54] !== debouncedParams.page || $[55] !== debouncedParams.page_size || $[56] !== isLoading || $[57] !== members || $[58] !== setPage) {
    t20 = () => {
      if (isLoading) {
        return;
      }
      if (!members?.count) {
        setPage(1);
        setTotalPages(1);
        return;
      }
      const pagesCount = Math.ceil(members.count / debouncedParams.page_size);
      if (pagesCount < debouncedParams.page) {
        setPage(pagesCount);
      }
      setTotalPages(pagesCount);
    };
    $[54] = debouncedParams.page;
    $[55] = debouncedParams.page_size;
    $[56] = isLoading;
    $[57] = members;
    $[58] = setPage;
    $[59] = t20;
  } else {
    t20 = $[59];
  }
  const t21 = members?.count;
  let t22;
  if ($[60] !== debouncedParams.page || $[61] !== debouncedParams.page_size || $[62] !== isLoading || $[63] !== setPage || $[64] !== t21) {
    t22 = [debouncedParams.page, debouncedParams.page_size, isLoading, t21, setPage];
    $[60] = debouncedParams.page;
    $[61] = debouncedParams.page_size;
    $[62] = isLoading;
    $[63] = setPage;
    $[64] = t21;
    $[65] = t22;
  } else {
    t22 = $[65];
  }
  useEffect(t20, t22);
  let t23;
  if ($[66] !== router) {
    t23 = () => router.push("/network/nerd");
    $[66] = router;
    $[67] = t23;
  } else {
    t23 = $[67];
  }
  const goToNerd = t23;
  let t24;
  if ($[68] === Symbol.for("react.memo_cache_sentinel")) {
    t24 = <h1 className="tw-text-xl tw-font-semibold tw-text-iron-50 tw-mb-0">Network</h1>;
    $[68] = t24;
  } else {
    t24 = $[68];
  }
  let t25;
  if ($[69] === Symbol.for("react.memo_cache_sentinel")) {
    t25 = <span>Nerd view</span>;
    $[69] = t25;
  } else {
    t25 = $[69];
  }
  let t26;
  if ($[70] === Symbol.for("react.memo_cache_sentinel")) {
    t26 = <svg className="-tw-mr-1.5 tw-h-5 tw-w-5" viewBox="0 0 20 20" fill="currentColor" aria-hidden="true"><path fillRule="evenodd" d="M7.21 14.77a.75.75 0 01.02-1.06L11.168 10 7.23 6.29a.75.75 0 111.04-1.08l4.5 4.25a.75.75 0 010 1.08l-4.5 4.25a.75.75 0 01-1.06-.02z" clipRule="evenodd" /></svg>;
    $[70] = t26;
  } else {
    t26 = $[70];
  }
  let t27;
  if ($[71] !== goToNerd) {
    t27 = <div className="tw-flex tw-items-center tw-justify-between">{t24}<div className="tw-inline-flex tw-space-x-3 tw-items-center tw-ml-auto"><button type="button" className="tw-text-sm tw-font-semibold tw-inline-flex tw-items-center tw-rounded-lg tw-bg-iron-800 tw-px-3 tw-py-2 tw-text-iron-200 focus:tw-outline-none focus:tw-ring-1 focus:tw-ring-inset focus:tw-ring-primary-400 tw-border-0 tw-ring-1 tw-ring-inset tw-ring-iron-700 hover:tw-bg-iron-700 focus:tw-z-10 tw-transition tw-duration-300 tw-ease-out" onClick={goToNerd}>{t25}{t26}</button></div></div>;
    $[71] = goToNerd;
    $[72] = t27;
  } else {
    t27 = $[72];
  }
  let t28;
  if ($[73] !== isFetching || $[74] !== isLoading || $[75] !== members || $[76] !== params.page_size || $[77] !== params.sort || $[78] !== params.sort_direction || $[79] !== setPage || $[80] !== setSortBy || $[81] !== totalPages) {
    t28 = members ? <div><div className="tailwind-scope tw-mt-4 lg:tw-mt-6 tw-flow-root"><div className="sm:tw-overflow-auto tw-bg-iron-950 tw-shadow sm:tw-border sm:tw-border-solid sm:tw-border-iron-700 tw-rounded-lg sm:tw-divide-y sm:tw-divide-solid sm:tw-divide-iron-800"><CommunityMembersTable members={members.data} activeSort={params.sort} sortDirection={params.sort_direction} page={members.page} pageSize={params.page_size} isLoading={isFetching} onSort={setSortBy} /></div></div>{totalPages > 1 && <CommonTablePagination currentPage={members.page} setCurrentPage={setPage} totalPages={totalPages} haveNextPage={members.next} small={false} loading={isLoading} />}</div> : <div className="tw-h-screen tw-w-full"><CommonCardSkeleton /></div>;
    $[73] = isFetching;
    $[74] = isLoading;
    $[75] = members;
    $[76] = params.page_size;
    $[77] = params.sort;
    $[78] = params.sort_direction;
    $[79] = setPage;
    $[80] = setSortBy;
    $[81] = totalPages;
    $[82] = t28;
  } else {
    t28 = $[82];
  }
  let t29;
  if ($[83] !== t27 || $[84] !== t28) {
    t29 = <div>{t27}{t28}</div>;
    $[83] = t27;
    $[84] = t28;
    $[85] = t29;
  } else {
    t29 = $[85];
  }
  return t29;
}